import type { BriefingOptions } from '_core/context/public-app-context';
import { getLocalizedUrl } from '_core/utils/public-links/getLocalizedUrl';
import { Agencies, AnchorType, Link, Links, Page, TopicContent } from '_types/public-api';

export type LinkDetails = {
  metadata: {
    hreflang: string;
    title_tag: string;
    metadescription: string;
  } | null;
  anchor: string;
  position: number;
  url: string;
};

export const filteredLinks = (links: Links | undefined, type: string): LinkDetails[] | undefined => {
  const filteredLinks = links?.data
    .filter((data) => data.attributes.category.includes(type))
    .sort((a, b) => {
      if (a.attributes.position < b.attributes.position) return -1;
      else return 0;
    });

  return filteredLinks?.map((link) => {
    const findRelation = links?.included.find((page) => page.id === link.relationships.to_page.data.id);

    return {
      metadata: link.attributes.metadata,
      anchor: link.attributes.anchor,
      position: link.attributes.position,
      url: getLocalizedUrl({ ...findRelation?.attributes, link: findRelation?.attributes.path } as AnchorType),
    };
  });
};

export const omitLinkCategory = (links: Links, type: string): Links => {
  const isOmitted = (link: Link) => link.attributes.category.includes(type);
  const keptLinks = links.data.filter((link) => !isOmitted(link));
  const keptPageIds = new Set(keptLinks.map((link) => link.relationships.to_page.data.id));
  const omittedPageIds = new Set(links.data.filter(isOmitted).map((link) => link.relationships.to_page.data.id));

  return {
    ...links,
    data: keptLinks,
    included: links.included.filter((page) => keptPageIds.has(page.id) || !omittedPageIds.has(page.id)),
  };
};

export const getIncludedAgenciesSlugs = (ssrAgencies: Agencies | undefined) => {
  return (
    ssrAgencies?.included &&
    ssrAgencies.included.map((obj) => {
      if (obj.attributes.team_size === null) return { ...obj, attributes: { ...obj.attributes, team_size: 0 } };
      return obj?.attributes?.slug || 0;
    })
  );
};

// Modifiers are created and managed by EPO
// Use this value to override modifiers and alway display default values
export const SHOW_MODIFIERS = true;

/** Current 1-based page number for a directory page, read from the `page` query param. */
export const getCurrentPage = (query: string | undefined): number =>
  Number(new URLSearchParams(query ?? '').get('page') ?? '1');

export const getServiceTopicId = (page: Page) => {
  return (
    page?.included.find(
      (item) => item.type === 'topic' && ['skill', 'expertise'].includes(item.attributes.external_type),
    ) as TopicContent | undefined
  )?.attributes?.graphql_id;
};

export const getPageBriefingOptions = (page: Page): BriefingOptions => {
  const briefingOptions = page.data.attributes.briefing_options;
  const expertise = briefingOptions?.expertise;
  const expertiseId = expertise != null ? Number(expertise.id) : undefined;

  return {
    page: page.data.attributes.frontend_type,
    address: briefingOptions?.location?.address,
    placeId: briefingOptions?.location?.place_id,
    expertise: expertiseId,
    expertiseObject: expertise != null && expertiseId != null ? { expertiseId, ...expertise } : undefined,
    skills: briefingOptions?.skills,
    skillObjects: briefingOptions?.skill_objects,
    modifiers: SHOW_MODIFIERS
      ? {
          matching: briefingOptions?.modifier_matching,
          rightProvider: briefingOptions?.modifier_right_provider,
        }
      : undefined,
  };
};
